import React, { useState, useEffect } from "react";
import { Box, Grid, Typography, Card, Chip } from "@mui/material";
import { Bell, Clock } from "lucide-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getNotificationsByType, marksAllRead } from "../../services/Notification";
import { openSnackAlert } from "../../helper";
import AppLoader from "../../components/appLoader";

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchNotifications();
    markAllAsRead();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await getNotificationsByType();
      if (response?.status === 200 || response?.status === 201) {
        const list = response.data.data || [];
        // Filter: only notifications with senderRole (worker or admin)
        const filteredList = list.filter(
          (item) => item.senderRole && (item.senderRole === "worker" || item.senderRole === "admin")
        );
        setNotifications(filteredList);
      } else {
        openSnackAlert(
          response?.data?.message || "Failed to fetch notifications",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      openSnackAlert("Error loading notifications", "error");
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await marksAllRead();
      if (response?.status === 200 || response?.status === 201) {
        // Successfully marked all as read
        console.log("All notifications marked as read");
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
      // Don't show error to user as this is a background operation
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("DD MMM YYYY, hh:mm A");
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).fromNow();
  };

  // Redirect based on notification type
  const handleNotificationClick = (notification) => {
    const { notificationType, type, senderRole } = notification;

    // Group chat → chats page
    if (notificationType === "group_chat") {
      navigate("/chats");
      return;
    }

    // Announcement → announcements page
    if (type === "announcement") {
      navigate("/announcements");
      return;
    }

    // Activity notification → submitted forms page
    if (notificationType === "activity") {
      navigate("/submitted-forms");
      return;
    }

    // Job assignment → task management page
    if (notificationType === "job_assignment") {
      navigate("/task-management");
      return;
    }
  };

  return (
    <Box
      bgcolor="primary.bgLight"
      padding={"20px"}
      borderRadius={"22px"}
      mt={0}
    >
      <AppLoader isVisible={loading} />
      
      {/* Header Section */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                background: "linear-gradient(90deg, #225B27, #053608)",
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Bell size={24} color="#fff" />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} color="#225B27">
                Notifications
              </Typography>
              <Typography variant="body2" color="#666" fontSize={14}>
                View all your notifications
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Chip
              label={`${notifications.length} Notification${notifications.length !== 1 ? 's' : ''}`}
              sx={{
                background: "linear-gradient(90deg, #225B27, #053608)",
                color: "#fff",
                fontWeight: 600,
                fontSize: "14px",
                height: "36px",
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Notifications List */}
      {notifications.length > 0 ? (
        <Grid container spacing={2}>
          {notifications.map((notification, idx) => (
            <Grid item xs={12} key={notification._id || idx}>
              <Card
                sx={{
                  borderRadius: "16px",
                  border: "1px solid #e0e0e0",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0px 4px 16px rgba(34, 91, 39, 0.15)",
                    transform: "translateY(-2px)",
                  },
                  overflow: "hidden",
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    p: 3,
                  }}
                >
                  {/* Icon Section */}
                  <Box
                    sx={{
                      minWidth: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      background: notification.isRead
                        ? "linear-gradient(90deg, #e8f5e9, #c8e6c9)"
                        : "linear-gradient(90deg, #225B27, #053608)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Bell
                      size={20}
                      color={notification.isRead ? "#225B27" : "#fff"}
                    />
                  </Box>

                  {/* Content Section */}
                  <Box sx={{ flex: 1 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={1}
                    >
                      <Typography
                        variant="h6"
                        fontSize={16}
                        fontWeight={600}
                        color="#225B27"
                        sx={{
                          textDecoration: notification.isRead
                            ? "none"
                            : "none",
                        }}
                      >
                        {notification.title || "No Title"}
                      </Typography>
                      {!notification.isRead && (
                        <Chip
                          label="New"
                          size="small"
                          sx={{
                            backgroundColor: "#FF5722",
                            color: "#fff",
                            fontSize: "10px",
                            height: "20px",
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      color="#666"
                      fontSize={14}
                      sx={{ mb: 2, lineHeight: 1.6 }}
                    >
                      {notification.body || "No description available."}
                    </Typography>

                    {/* Date Section */}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ mt: 2 }}
                    >
                      <Clock size={14} color="#999" />
                      <Typography
                        variant="caption"
                        color="#999"
                        fontSize={12}
                        fontWeight={500}
                      >
                        {formatDate(notification.createdAt)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="#999"
                        fontSize={12}
                        sx={{ ml: 1 }}
                      >
                        • {getTimeAgo(notification.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Gradient Bottom Border */}
                <Box
                  sx={{
                    height: "4px",
                    background: "linear-gradient(90deg, #225B27, #053608)",
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <Box
            sx={{
              background: "linear-gradient(90deg, #e8f5e9, #c8e6c9)",
              borderRadius: "50%",
              p: 3,
              mb: 2,
            }}
          >
            <Bell size={48} color="#225B27" />
          </Box>
          <Typography
            variant="h6"
            color="#225B27"
            fontWeight={600}
            mb={1}
          >
            No Notifications
          </Typography>
          <Typography variant="body2" color="#666" fontSize={14}>
            You're all caught up! No new notifications.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Notifications;
